import { useState } from 'react';
import { createPortal } from 'react-dom';
import { ShieldCheck, X } from 'lucide-react';

// Lawful-use guard (Sprint battle-ready, Phase 5.4). A pre-hearing checklist that
// appears before the first Start Listening of a session, drawn from the legal and
// ethical anchors in docs/07-courtroom-protocol.md. The operator must confirm each
// item before listening begins. This is a reminder, not legal advice.

const ITEMS = [
  'I have leave to use Court Copilot in this hearing, or I am using it only during breaks and outside the courtroom.',
  'The laptop screen faces only me. It is not visible to the bench, the witness, or the opposing party.',
  'Court Copilot transcribes locally in the browser. It does not record or store audio. I have any consent required for transcription.',
  'I will verify every citation and fact before relying on it. The duty of candour to the court is mine alone.',
];

interface PreHearingModalProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function PreHearingModal({ open, onConfirm, onCancel }: PreHearingModalProps) {
  const [checked, setChecked] = useState<boolean[]>(() => ITEMS.map(() => false));
  if (!open) {
    return null;
  }
  const allChecked = checked.every(Boolean);
  const toggle = (i: number): void =>
    setChecked((prev) => prev.map((v, j) => (j === i ? !v : v)));

  // Render via a portal to <body>. The modal is mounted inside the Header, which
  // uses backdrop-blur (backdrop-filter) — that makes the Header a containing
  // block for position:fixed descendants, so without the portal the modal is
  // sized/positioned relative to the 73px header, not the viewport (clipping the
  // first item and breaking the buttons). The portal escapes that context.
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Pre-hearing checklist"
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-stone-800 bg-stone-950 p-5 shadow-2xl">
        <div className="flex items-start justify-between gap-3">
          <h2 className="flex items-center gap-2 font-display text-xl italic text-amber-500">
            <ShieldCheck className="h-5 w-5" aria-hidden />
            Before you start listening
          </h2>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Cancel"
            className="rounded p-1 text-stone-500 hover:text-stone-300"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-2 text-sm text-stone-400">
          Confirm each item. Court Copilot augments your judgement; it does not replace it.
        </p>
        <ul className="mt-4 space-y-3">
          {ITEMS.map((item, i) => (
            <li key={i}>
              <label className="flex cursor-pointer items-start gap-3 text-sm text-stone-200">
                <input
                  type="checkbox"
                  checked={checked[i] ?? false}
                  onChange={() => toggle(i)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-amber-600"
                />
                <span>{item}</span>
              </label>
            </li>
          ))}
        </ul>
        <div className="mt-5 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="text-sm text-stone-400 hover:text-stone-200"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!allChecked}
            onClick={onConfirm}
            className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-stone-950 transition-colors hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Acknowledge and start listening
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
